import React from 'react';
import {
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Typography,
  Chip,
  IconButton,
  useTheme,
  Box,
} from '@mui/material';
import { MoreVert } from '@mui/icons-material';

interface Assessment {
  _id: string;
  title: string;
  description: string;
  category: string | { _id: string; name: string };
  difficulty: string;
  isActive: boolean;
  configuration: {
    questionCount: number;
  };
}

interface Question {
  _id: string;
  question: string;
  category: string | { _id: string; name: string };
  difficulty: string;
  correctAnswer: string;
  createdAt: string;
}

interface ManagementTableProps {
  data: Assessment[] | Question[];
  type: 'assessments' | 'questions';
  onMenuClick: (event: React.MouseEvent<HTMLElement>, item: any) => void;
  getDifficultyColor: (difficulty: string) => any;
}

export const ManagementTable: React.FC<ManagementTableProps> = ({
  data,
  type,
  onMenuClick,
  getDifficultyColor,
}) => {
  const theme = useTheme();

  if (!data || data.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 6 }}>
        <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
          No {type} available
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {type === 'assessments' 
            ? 'Create your first assessment to get started.' 
            : 'Add your first question to get started.'
          }
        </Typography>
      </Box>
    );
  }

  return (
    <TableContainer component={Paper} elevation={0} sx={{ border: `1px solid ${theme.palette.divider}` }}>
      <Table>
        <TableHead>
          <TableRow>
            {type === 'assessments' ? (
              <>
                <TableCell>Title</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Difficulty</TableCell>
                <TableCell>Questions</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </>
            ) : (
              <>
                <TableCell>Question</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Difficulty</TableCell>
                <TableCell>Correct Answer</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Actions</TableCell>
              </>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((item) => (
            <TableRow key={item._id}>
              {type === 'assessments' ? (
                <>
                  <TableCell>
                    <Typography variant="subtitle2">{(item as Assessment).title}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {(item as Assessment).description}
                    </Typography>
                  </TableCell>
                  <TableCell>{typeof item.category === 'object' ? item.category?.name : item.category}</TableCell>
                  <TableCell>
                    <Chip 
                      label={item.difficulty} 
                      color={getDifficultyColor(item.difficulty)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{(item as Assessment).configuration.questionCount}</TableCell>
                  <TableCell>
                    <Chip 
                      label={(item as Assessment).isActive ? 'Active' : 'Inactive'} 
                      color={(item as Assessment).isActive ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton onClick={(e) => onMenuClick(e, item)}>
                      <MoreVert />
                    </IconButton>
                  </TableCell>
                </>
              ) : (
                <>
                  <TableCell>
                    <Typography variant="subtitle2" sx={{ maxWidth: 300 }}>
                      {(item as Question).question}
                    </Typography>
                  </TableCell>
                  <TableCell>{typeof item.category === 'object' ? item.category?.name : item.category}</TableCell>
                  <TableCell>
                    <Chip 
                      label={item.difficulty} 
                      color={getDifficultyColor(item.difficulty)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{(item as Question).correctAnswer}</TableCell>
                  <TableCell>
                    {new Date((item as Question).createdAt).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <IconButton onClick={(e) => onMenuClick(e, item)}>
                      <MoreVert />
                    </IconButton>
                  </TableCell>
                </>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};